import DataHash from '../classes/data-hash';

/**
 * Class representing the HousingReport Geography Selection.
 */
export default class HousingReportGeoSelect {
    DataPageInstance;

    /**
     * Location data received from the server.
     * @type {Object}
     */
    geoData;

    /**
     * button to clear Geo selectors
     */
    resetBtn;

    staticSelectors = [
        {
            name: 'State',
            selectorId: 'area_type_1_select',
            geoTypeId: 1,
            hashKey: 'State',
            choice: {},
        },
        {
            name: 'Metropolitan Statistical Area',
            selectorId: 'area_type_2_select',
            hashKey: 'MSA',
            geoTypeId: 2,
            choice: {},
        },
        {
            name: 'Metropolitan Division',
            selectorId: 'area_type_3_select',
            hashKey: 'MD',
            geoTypeId: 3,
            choice: {},
        },
    ];

    /**
     * Create a HousingReportGeoSelect instance.
     * @param {Object} DataPageInstance - The instance of the Data Page.
     * @param {Object} geoData - Location data received from the server.
     */
    constructor(DataPageInstance, geoData) {
        this.DataPageInstance = DataPageInstance;

        this.geoData = geoData;

        // Initialize selects
        this.initializeStaticSelects();

        this.initializeResetButton();

        this.HashInstance = new DataHash('#!/activity/', 'key_value');
        this.initializeQueryParameters();
    }

    /**
     * Initialize static select elements.
     */
    initializeStaticSelects() {
        this.staticSelectors.forEach((el) => {
            el['choice'] = new Choices(`#${el.selectorId}`, {
                position: 'bottom',
                itemSelectText: '',
                allowHTML: true,
                placeholderValue: el.name,
            });

            const data = this.geoData[el.selectorId];

            for (const key in data) {
                el['choice'].setChoices([
                    {
                        value: key,
                        label: data[key],
                    },
                ]);
            }

            // Event Handler For Select
            el['choice'].passedElement.element.addEventListener(
                'change',
                () => {
                    this.staticChoiceEventHandler(el);
                },
            );
        });
    }

    initializeResetButton() {
        const btn = document.querySelector('.js-geo-select-reset');
        if (!btn) {
            return;
        }
        this.resetBtn = btn;
        this.enableResetBtn();

        this.resetBtn.addEventListener(
            'click',
            this.resetBtnHandler.bind(this),
        );
    }

    /**
     * Event handler for the state select element.
     */
    staticChoiceEventHandler(el) {
        const type = el.name;
        this.staticSelectors.forEach((el) => {
            if (type === el.name) {
                return;
            }
            el.choice.setChoiceByValue('');
        });

        this.DataPageInstance.gridRefresh();
    }

    resetBtnHandler(event) {
        event.preventDefault();

        if (!this.resetBtn.classList.contains('active')) {
            return;
        }

        this.resetButtonEventStart();

        this.staticChoiceEventHandler({
            name: '',
        });
        this.DataPageInstance.showStartContent();
        this.resetBtnAvailability(false);

        this.resetButtonEventEnd();
    }

    /**
     * Initialize hash data based on the URL hash.
     */
    initializeQueryParameters() {
        const hashArray = this.HashInstance.getHash();
        const hashType = hashArray[0];
        const hashName = decodeURIComponent(hashArray[1]);

        if (!hashType || !hashName) {
            return;
        }

        this.staticSelectors.forEach((el) => {
            if (hashType !== el.hashKey) {
                return;
            }
            const data = this.geoData[el.selectorId];

            for (const key in data) {
                const placeName = data[key];

                // geo names might be not equal, like 'Amarilio' & 'Amarilio, TX'
                if (
                    !hashName.includes(placeName) &&
                    !placeName.includes(hashName)
                ) {
                    continue;
                }

                el['choice'].setChoiceByValue(key);
                // Trigger the 'change' event forcefully to allow other handlers to proceed.
                el['choice'].passedElement.element.dispatchEvent(
                    new Event('change'),
                );
                break;
            }
        });
    }

    /**
     * Set the URL query parameters based on the selected options.
     */
    setQueryParameters() {
        const requestParams = this.getRequestParams();
        if (requestParams.hashKey && requestParams.geoName) {
            this.HashInstance.setHash([
                requestParams.hashKey,
                requestParams.geoName,
            ]);
            return;
        }
        this.HashInstance.removeHash();
    }

    /**
     * Get the request parameters based on the selected options.
     * @returns {Object} - The request parameters.
     */
    getRequestParams() {
        let geoData = {};

        this.staticSelectors.forEach((el) => {
            const selectorId = el.choice.getValue(true);
            const selectorName = el.choice.getValue().label;
            if (selectorId) {
                geoData = {
                    geoId: selectorId,
                    geoName: selectorName,
                    geoTypeId: el.geoTypeId,
                    geoType: el.name,
                    hashKey: el.hashKey,
                };
            }
        });

        return geoData;
    }

    resetBtnAvailability(visible) {
        const el = this.resetBtn;

        if (visible) {
            el.classList.remove('!hidden');
        } else {
            el.classList.add('!hidden');
        }
    }

    /**
     * Disable all select elements.
     */
    disableSelects() {
        this.staticSelectors.forEach((el) => {
            el.choice.disable();
        });
        this.disableResetBtn();
    }

    /**
     * Enable all select elements.
     */
    enableSelects() {
        this.staticSelectors.forEach((el) => {
            el.choice.enable();
        });
        this.enableResetBtn();
    }

    enableResetBtn() {
        const el = this.resetBtn;

        el.classList.add('active');
        el.classList.remove('pointer-events-none');
    }

    disableResetBtn() {
        const el = this.resetBtn;
        el.classList.remove('active');
        el.classList.add('pointer-events-none');
    }

    resetButtonEventStart() {
        const event = new CustomEvent('resetData.Start');
        document.dispatchEvent(event);
    }

    resetButtonEventEnd() {
        const event = new CustomEvent('resetData.End');
        document.dispatchEvent(event);
    }
}
